import { useState, useCallback, useRef, useEffect } from 'react';

export interface ColumnWidths {
  [key: string]: number;
}

export const useColumnResize = (initialWidths: ColumnWidths, fixedColumns: string[] = []) => {
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(initialWidths);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback((columnId: string, startX: number) => {
    // Don't allow resizing fixed columns
    if (fixedColumns.includes(columnId)) return;
    
    setResizingColumn(columnId);
    startXRef.current = startX;
    startWidthRef.current = columnWidths[columnId] || 150;
  }, [columnWidths, fixedColumns]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizingColumn) return;

    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(80, startWidthRef.current + diff); // Minimum width of 80px

    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [resizingColumn]);

  const handleMouseUp = useCallback(() => {
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizingColumn, handleMouseMove, handleMouseUp]);

  return {
    columnWidths,
    resizingColumn,
    handleMouseDown,
    setColumnWidths
  };
};
